import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SongList } from '../components/SongList';
import { ArrowLeft, Phone, MapPin, Calendar, Volume2, VolumeX } from 'lucide-react';
export function HonorableMusicPage() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [audioStarted, setAudioStarted] = useState(false);
  const trackChosenRef = useRef(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.5;
    audio.loop = true;
    // Attempt to autoplay
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.then(() => setAudioStarted(true)).catch(() => {
        // Autoplay blocked - will play on first user interaction
        const startOnInteraction = () => {
          if (trackChosenRef.current) return;
          audio.play().then(() => setAudioStarted(true)).catch(() => {});
          document.removeEventListener('click', startOnInteraction);
          document.removeEventListener('scroll', startOnInteraction);
        };
        document.addEventListener('click', startOnInteraction, {
          once: true
        });
        document.addEventListener('scroll', startOnInteraction, {
          once: true
        });
      });
    }
    // Fade audio out on scroll
    const handleScroll = () => {
      if (!audio || isMuted) return;
      const scrollY = window.scrollY;
      const fadeStart = 200;
      const fadeEnd = 800;
      if (scrollY < fadeStart) {
        audio.volume = 0.5;
      } else if (scrollY > fadeEnd) {
        audio.volume = 0;
      } else {
        const progress = (scrollY - fadeStart) / (fadeEnd - fadeStart);
        audio.volume = 0.5 * (1 - progress);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      audio.pause();
    };
  }, [isMuted]);
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isMuted) {
      audio.volume = 0.5;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#0D121D'
  }} data-id="element-460" data-mp="23a8az8kzr3p9">
      <audio ref={audioRef} src="https://cdn.magicpatterns.com/uploads/e4ZF1Xcia2BBPx4J8edWyT/Bumpa_Hertz_Riddim.mp3" data-id="element-461" data-mp="2h5aks6syld7c" />

      <Header data-id="element-462" data-mp="2r1x2wntwhq5o" />

      {/* Mute toggle button - fixed */}
      <button onClick={toggleMute} className="fixed bottom-8 right-8 z-50 w-14 h-14 rounded-full bg-green-500 hover:bg-green-400 text-black shadow-lg shadow-green-500/50 flex items-center justify-center transition-colors" aria-label={isMuted ? 'Unmute audio' : 'Mute audio'} data-id="element-463" data-mp="2qzawiriqxgp1">
        {isMuted ? <VolumeX className="w-6 h-6" data-id="element-464" data-mp="33b3a4r41uy68" /> : <Volume2 className="w-6 h-6" data-id="element-465" data-mp="33cenninarkaj" />}
      </button>

      <main className="pt-32 pb-24" data-id="element-466" data-mp="389oaqrn7rjv7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-467" data-mp="38csn4cau1lwy">
          <a href="/saint-lucia" className="inline-flex items-center text-slate-300 hover:text-white transition-colors mb-12" data-id="element-468" data-mp="2tks32vp2k8v4">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-469" data-mp="2klevk8ki9ud1" />
            Back to Saint Lucia
          </a>

          {/* Hero Section with Artist Photo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24" data-id="element-470" data-mp="2pkzx8qr28gtg">
            {/* Artist Photo with green glow */}
            <div className="relative order-2 lg:order-1" data-id="element-471" data-mp="2apbvd897s5sq">
              <div className="absolute -inset-4 bg-green-500/30 blur-3xl rounded-full" data-id="element-472" data-mp="2fmtvc9597aol" />
              <div className="relative rounded-3xl overflow-hidden border-2 border-green-500/50" data-id="element-473" data-mp="8wnper56r499">
                <img src="https://cdn.magicpatterns.com/uploads/vcnsXBbGS4zPtRc55VDBKi/Honorable_Music_-Brand_Image_1.png" alt="Honorable Music Artist" className="w-full h-auto" data-id="element-474" data-mp="dtmh731dmz4a" />
              </div>
            </div>

            {/* Artist Info */}
            <div className="order-1 lg:order-2 text-center lg:text-left" data-id="element-475" data-mp="20vknfwaw7geq">
              <div className="mb-6 flex justify-center lg:justify-start" data-id="element-476" data-mp="1vwzl9rc2ymnn">
                <div className="bg-white rounded-2xl p-4 inline-block" data-id="element-477" data-mp="1qyprnl607u45">
                  <img src="https://cdn.magicpatterns.com/uploads/kNU33mM35Pd4B3EEWnk3ED/Honorable_Music_Logo-01.png" alt="Honorable Music Logo" className="h-24 w-auto" data-id="element-478" data-mp="1r0spgrdeerjf" />
                </div>
              </div>

              <div className="inline-block px-4 py-1.5 rounded-full bg-green-500/20 text-green-400 text-sm font-bold mb-4 uppercase tracking-wider" data-id="element-479" data-mp="1d5gjtoxpfgnq">
                Soca • Saint Lucia
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight" data-id="element-480" data-mp="13bgfhpa8yfg2">
                <span className="text-green-400" data-id="element-481" data-mp="t7t4tsg2vfsa">Honorable</span>
                <br data-id="element-482" data-mp="y2yn8ha9ucbm" />
                Music
              </h1>

              <p className="text-2xl text-slate-300 mb-8 italic" data-id="element-483" data-mp="13331s1rmcwz7">
                "Start a vibe. Book a performance."
              </p>

              <p className="text-lg text-slate-400 leading-relaxed mb-8" data-id="element-484" data-mp="1827eh2dr5cua">
                A new Soca artist rising out of Saint Lucia, bringing infectious
                rhythms and unforgettable performances to every stage.
              </p>

              <a href="#book" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-bold rounded-full text-black bg-green-400 hover:bg-green-300 transition-colors shadow-lg shadow-green-500/30" data-id="element-485" data-mp="kdepjpvjs596">
                <Calendar className="w-5 h-5" data-id="element-486" data-mp="3nrr7ifuva6ct" />
                Book a Performance
              </a>
            </div>
          </div>

          {/* Singles Section */}
          <div className="mb-24 max-w-3xl mx-auto" data-id="element-487" data-mp="3kqxsw9yw9dyl">
            <div className="text-center mb-10" data-id="element-488" data-mp="3ft5o4fd5hxcz">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-3" data-id="element-489" data-mp="3fse667ch7y56">
                The Singles
              </h2>
              <p className="text-lg text-slate-400" data-id="element-490" data-mp="3uk4udjqik9te">
                The tracks taking over the islands — listen and download right
                here
              </p>
            </div>

            <SongList onTrackPlay={() => {
            trackChosenRef.current = true;
            audioRef.current?.pause();
          }} data-id="element-491" data-mp="31tmuerl36ok7" />
          </div>

          {/* Booking / Contact Section */}
          <div id="book" className="bg-gradient-to-br from-green-600/20 via-slate-900/40 to-slate-900/60 backdrop-blur rounded-3xl border border-green-500/30 p-12 max-w-5xl mx-auto" data-id="element-492" data-mp="36shdlumyuf4u">
            <div className="text-center mb-10" data-id="element-493" data-mp="m2rzxky00zdq">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-3" data-id="element-494" data-mp="m1yviqmiilyr">
                Book the Vibe
              </h2>
              <p className="text-lg text-slate-300" data-id="element-495" data-mp="ykumf5hvqn2f">
                Reach out directly to book Honorable Music for your next event
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-496" data-mp="kmw6vwrtoklc">
              <a href="tel:+13475836724" className="bg-white/5 hover:bg-white/10 border border-slate-700 hover:border-green-400 rounded-2xl p-8 flex items-start gap-4 transition-all" data-id="element-497" data-mp="onpusq59lze8">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0" data-id="element-498" data-mp="1hakhzutgvcve">
                  <MapPin className="w-6 h-6 text-green-400" data-id="element-499" data-mp="17lj0g079wnfb" />
                </div>
                <div data-id="element-500" data-mp="17jfychsuj3u8">
                  <div className="text-sm text-green-400 font-bold uppercase tracking-wider mb-1" data-id="element-501" data-mp="1vb4vqckiyii1">
                    New York
                  </div>
                  <div className="text-2xl font-bold text-white flex items-center gap-2" data-id="element-502" data-mp="203ekvqdtfej9">
                    <Phone className="w-5 h-5" data-id="element-503" data-mp="1bbz4nowwu6go" />
                    +1 (347) 583-6724
                  </div>
                </div>
              </a>

              <a href="tel:+17587198554" className="bg-white/5 hover:bg-white/10 border border-slate-700 hover:border-green-400 rounded-2xl p-8 flex items-start gap-4 transition-all" data-id="element-504" data-mp="11ldztjqn85pb">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0" data-id="element-505" data-mp="1uabhplqy00hj">
                  <MapPin className="w-6 h-6 text-green-400" data-id="element-506" data-mp="1pc9uqpqebh0s" />
                </div>
                <div data-id="element-507" data-mp="1kaudz9v93wo7">
                  <div className="text-sm text-green-400 font-bold uppercase tracking-wider mb-1" data-id="element-508" data-mp="1kaawfu9ijaap">
                    Saint Lucia
                  </div>
                  <div className="text-2xl font-bold text-white flex items-center gap-2" data-id="element-509" data-mp="8x8eaurrmcof">
                    <Phone className="w-5 h-5" data-id="element-510" data-mp="3xvqa6p5qzk9" />
                    +1 (758) 719-8554
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-511" data-mp="2kjiw1m7y9c60" />
    </div>;
}